'use client';

import { useState, useEffect } from 'react';
import InsightCard from './InsightCard';
import CategoryFilter from './CategoryFilter';
import SearchBar from './SearchBar';
import FeaturedInsight from './FeaturedInsight';
import NewsletterSubscription from './NewsletterSubscription';

interface Insight {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  slug: string;
  featured?: boolean;
}

const mockInsights: Insight[] = [
  {
    id: '1',
    title: 'ICSID Convention Amendments: Implications for Investment Treaty Arbitration',
    excerpt: 'Analysis of the 2022 ICSID Rules amendments and their impact on procedural efficiency, cost allocation, and transparency requirements in investment arbitration proceedings.',
    category: 'Investment Arbitration',
    author: 'Dr. Catherine Moreau',
    date: '15 December 2025',
    readTime: '12 min read',
    slug: 'icsid-convention-amendments-2025',
    featured: true
  },
  {
    id: '2',
    title: 'Enforcement of Arbitral Awards Under the New York Convention: Recent Developments',
    excerpt: 'Comprehensive review of recent court decisions interpreting public policy exceptions and their implications for cross-border enforcement strategies.',
    category: 'Commercial Arbitration',
    author: 'Jean-Philippe Dubois',
    date: '10 December 2025',
    readTime: '15 min read',
    slug: 'nyc-enforcement-developments'
  },
  {
    id: '3',
    title: 'Third-Party Funding in International Arbitration: Regulatory Landscape 2025',
    excerpt: 'Examination of emerging disclosure requirements and ethical considerations surrounding third-party funding arrangements across major arbitration jurisdictions.',
    category: 'Regulatory Updates',
    author: 'Dr. Catherine Moreau',
    date: '5 December 2025',
    readTime: '10 min read',
    slug: 'tpf-regulatory-landscape-2025'
  },
  {
    id: '4',
    title: 'Jurisdictional Challenges in Multi-Tier Dispute Resolution Clauses',
    excerpt: 'Strategic analysis of recent tribunal decisions on the enforceability of escalation clauses and their impact on arbitration commencement timing.',
    category: 'Procedural Guides',
    author: 'Alexandre Laurent',
    date: '1 December 2025',
    readTime: '14 min read',
    slug: 'multi-tier-jurisdiction-challenges'
  },
  {
    id: '5',
    title: 'Climate Change and Investment Treaty Arbitration: Emerging Trends',
    excerpt: 'Analysis of how environmental regulations are reshaping fair and equitable treatment standards in bilateral investment treaty disputes.',
    category: 'Investment Arbitration',
    author: 'Dr. Catherine Moreau',
    date: '28 November 2025',
    readTime: '16 min read',
    slug: 'climate-change-investment-treaties'
  },
  {
    id: '6',
    title: 'ICC Arbitration Rules 2021: Practical Implementation Insights',
    excerpt: 'Detailed examination of expedited procedures, emergency arbitrator provisions, and case management conference requirements under the revised ICC Rules.',
    category: 'Commercial Arbitration',
    author: 'Jean-Philippe Dubois',
    date: '22 November 2025',
    readTime: '11 min read',
    slug: 'icc-rules-2021-implementation'
  },
  {
    id: '7',
    title: 'Cybersecurity Considerations in Virtual Arbitration Proceedings',
    excerpt: 'Best practices for maintaining confidentiality and data security in remote hearings, including encryption protocols and platform selection criteria.',
    category: 'Procedural Guides',
    author: 'Alexandre Laurent',
    date: '18 November 2025',
    readTime: '9 min read',
    slug: 'cybersecurity-virtual-arbitration'
  },
  {
    id: '8',
    title: 'Sovereign Immunity Defenses in Investment Arbitration: Recent Jurisprudence',
    excerpt: 'Comprehensive analysis of tribunal approaches to sovereign immunity claims and their implications for enforcement of arbitral awards against state entities.',
    category: 'Investment Arbitration',
    author: 'Dr. Catherine Moreau',
    date: '12 November 2025',
    readTime: '13 min read',
    slug: 'sovereign-immunity-recent-cases'
  },
  {
    id: '9',
    title: 'Expert Witness Testimony: Standards and Best Practices in International Arbitration',
    excerpt: 'Examination of tribunal expectations for expert reports, cross-examination techniques, and the evolving role of party-appointed experts.',
    category: 'Procedural Guides',
    author: 'Alexandre Laurent',
    date: '8 November 2025',
    readTime: '12 min read',
    slug: 'expert-witness-best-practices'
  }
];

const categories = [
  'All Insights',
  'Investment Arbitration',
  'Commercial Arbitration',
  'Regulatory Updates',
  'Procedural Guides'
];

const InsightsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All Insights');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredInsights, setFilteredInsights] = useState(mockInsights);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isHydrated) {
      let filtered = mockInsights;

      if (activeCategory !== 'All Insights') {
        filtered = filtered.filter(insight => insight.category === activeCategory);
      }

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(insight =>
          insight.title.toLowerCase().includes(query) ||
          insight.excerpt.toLowerCase().includes(query) ||
          insight.category.toLowerCase().includes(query) ||
          insight.author.toLowerCase().includes(query)
        );
      }

      setFilteredInsights(filtered);
    }
  }, [activeCategory, searchQuery, isHydrated]);

  const handleCategoryChange = (category: string) => {
    if (isHydrated) {
      setActiveCategory(category);
    }
  };

  const handleSearch = (query: string) => {
    if (isHydrated) {
      setSearchQuery(query);
    }
  };

  const featuredInsight = mockInsights.find(insight => insight.featured);
  const regularInsights = filteredInsights.filter(insight => !insight.featured);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-32 pb-2xl">
        <div className="max-w-7xl mx-auto px-xl">
          <div className="mb-2xl">
            <h1 className="text-5xl font-light tracking-wide text-primary mb-md">
              Legal Insights
            </h1>
            <p className="text-lg text-text-secondary max-w-3xl leading-relaxed">
              Authoritative analysis on international arbitration, regulatory developments, and procedural innovation from our expert practitioners.
            </p>
          </div>

          <SearchBar onSearch={handleSearch} />
          <CategoryFilter 
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
          />

          {featuredInsight && <FeaturedInsight insight={featuredInsight} />}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-lg mb-2xl">
            {mockInsights.filter(i => !i.featured).slice(0, 6).map((insight) => (
              <InsightCard key={insight.id} insight={insight} />
            ))}
          </div>

          <NewsletterSubscription />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-32 pb-2xl">
      <div className="max-w-7xl mx-auto px-xl">
        <div className="mb-2xl">
          <h1 className="text-5xl font-light tracking-wide text-primary mb-md">
            Legal Insights
          </h1>
          <p className="text-lg text-text-secondary max-w-3xl leading-relaxed">
            Authoritative analysis on international arbitration, regulatory developments, and procedural innovation from our expert practitioners.
          </p>
        </div>

        <SearchBar onSearch={handleSearch} />
        <CategoryFilter 
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange}
        />

        {featuredInsight && activeCategory === 'All Insights' && !searchQuery && (
          <FeaturedInsight insight={featuredInsight} />
        )}

        {regularInsights.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-lg mb-2xl">
            {regularInsights.map((insight) => (
              <InsightCard key={insight.id} insight={insight} />
            ))}
          </div>
        ) : (
          <div className="text-center py-3xl">
            <p className="text-lg text-text-secondary">
              No insights found matching your criteria. Please try adjusting your search or filter selection.
            </p>
          </div>
        )}

        <NewsletterSubscription />
      </div>
    </div>
  );
};

export default InsightsInteractive;